import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import {
  Risk,
  RiskStatus,
  RiskTolerability,
  RiskAssessment,
  RiskControl,
  RiskReview,
  RiskHazardLink,
  RiskComment,
  RiskStatusHistory,
} from '../types';
import { riskService } from '../riskService';
import { hazardService } from '../../hazards/hazardService';
import { Hazard } from '../../hazards/types';
import { BowtieTab } from '../bowtie/BowtieTab';
import { useEscapeKey } from '../../../../shared/useGlobalModalEscape';
import { useDialog } from '../../../../shared/DialogContext';

interface RiskDetailModalProps {
  riskId: string;
  onClose: () => void;
  onUpdated: () => void;
}

type TabType = 'overview' | 'bowtie' | 'controls' | 'reassessment' | 'reviews' | 'hazards' | 'comments' | 'history';

const ALL_STATUSES: RiskStatus[] = [
  'DRAFT',
  'IDENTIFIED',
  'ASSESSED',
  'MITIGATION_PLANNED',
  'MITIGATION_IN_PROGRESS',
  'RESIDUAL_ACCEPTED',
  'MONITORED',
  'CLOSED',
];

const SEVERITY_OPTIONS = [
  { value: 5, label: '5 - Catastrophic' },
  { value: 4, label: '4 - Major' },
  { value: 3, label: '3 - Moderate' },
  { value: 2, label: '2 - Minor' },
  { value: 1, label: '1 - Negligible' },
];

const LIKELIHOOD_OPTIONS = [
  { value: 'A', label: 'A - Frequent' },
  { value: 'B', label: 'B - Probable' },
  { value: 'C', label: 'C - Remote' },
  { value: 'D', label: 'D - Extremely Improbable (Unlikely)' },
  { value: 'E', label: 'E - Extremely Improbable' },
];

export const RiskDetailModal: React.FC<RiskDetailModalProps> = ({
  riskId,
  onClose,
  onUpdated,
}) => {
  useEscapeKey(onClose);
  const { showError, showSuccess, showWarning } = useDialog();

  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [risk, setRisk] = useState<Risk | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  // Status transition state
  const [selectedToStatus, setSelectedToStatus] = useState<RiskStatus>('ASSESSED');
  const [transitionReason, setTransitionReason] = useState('');

  // Control Form State
  const [controlTitle, setControlTitle] = useState('');
  const [controlType, setControlType] = useState<'PREVENTIVE' | 'MITIGATIVE' | 'RECOVERY'>('PREVENTIVE');
  const [controlDesc, setControlDesc] = useState('');
  const [controlDept, setControlDept] = useState('');
  const [controlEffectiveness, setControlEffectiveness] = useState(80);
  const [controlThreatId, setControlThreatId] = useState('');
  const [controlConsequenceId, setControlConsequenceId] = useState('');
  const [bowtieThreats, setBowtieThreats] = useState<{ id: string; label: string }[]>([]);
  const [bowtieConsequences, setBowtieConsequences] = useState<{ id: string; label: string }[]>([]);

  // Assessment Form State
  const [assessType, setAssessType] = useState<'RESIDUAL' | 'PERIODIC_REVIEW' | 'INITIAL'>('RESIDUAL');
  const [assessSev, setAssessSev] = useState(2);
  const [assessLik, setAssessLik] = useState('C');
  const [assessJustification, setAssessJustification] = useState('');
  const [assessorName, setAssessorName] = useState('Safety Auditor');

  // Review Form State
  const [reviewerName, setReviewerName] = useState('Accountable Manager');
  const [reviewType, setReviewType] = useState('Safety Committee');
  const [reviewVerdict, setReviewVerdict] = useState<
    'ACCEPTED' | 'REJECTED_FURTHER_MITIGATION_REQUIRED' | 'ESCALATED_TO_ACCOUNTABLE_MGR'
  >('ACCEPTED');
  const [reviewNotes, setReviewNotes] = useState('');

  // Hazard Link State
  const [allHazards, setAllHazards] = useState<Hazard[]>([]);
  const [hazardToLink, setHazardToLink] = useState('');
  const [hazardLinkNote, setHazardLinkNote] = useState('');

  // Comment State
  const [newComment, setNewComment] = useState('');

  const loadRisk = async () => {
    try {
      setLoading(true);
      const [data, bt] = await Promise.all([
        riskService.getRiskById(riskId),
        riskService.getBowtieDiagram(riskId).catch(() => null),
      ]);
      setRisk(data);
      if (data) {
        setSelectedToStatus(data.status);
      }
      if (bt && bt.nodes) {
        const tList = bt.nodes
          .filter((n) => n.type === 'threat')
          .map((n) => ({
            id: n.metadata?.causeId || n.id.replace('threat-', ''),
            label: n.label,
          }));
        const cList = bt.nodes
          .filter((n) => n.type === 'consequence')
          .map((n) => ({
            id: n.metadata?.consequenceId || n.id.replace('consequence-', ''),
            label: n.label,
          }));
        setBowtieThreats(tList);
        setBowtieConsequences(cList);
        if (!controlThreatId && tList.length > 0) setControlThreatId(tList[0].id);
        if (!controlConsequenceId && cList.length > 0) setControlConsequenceId(cList[0].id);
      }
    } catch (err: any) {
      console.error('Failed to load risk detail:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRisk();
    hazardService.getHazards({ limit: 100 })
      .then((res) => setAllHazards(res.items || []))
      .catch(() => {});
  }, [riskId]);

  const handleTransitionStatus = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!risk) return;
    if (selectedToStatus === risk.status) {
      showWarning('Selected status is the same as current status.', 'Status Unchanged');
      return;
    }
    if (!transitionReason.trim()) {
      showWarning('Please provide a reason or justification for status change.', 'Justification Required');
      return;
    }

    try {
      setActionLoading(true);
      await riskService.transitionStatus(risk.id, selectedToStatus, transitionReason.trim(), 'SAFETY_OFFICER');
      setTransitionReason('');
      await loadRisk();
      onUpdated();
      showSuccess(`Status transitioned to ${selectedToStatus} successfully!`);
    } catch (err: any) {
      showError(err, 'Risk Status Transition Failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddControl = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!risk || !controlTitle.trim()) return;

    try {
      setActionLoading(true);
      await riskService.addControl(
        risk.id,
        controlTitle.trim(),
        controlType,
        controlDesc.trim(),
        controlEffectiveness,
        controlDept.trim() || undefined,
        undefined,
        controlType === 'PREVENTIVE' && controlThreatId ? controlThreatId : undefined,
        (controlType === 'MITIGATIVE' || controlType === 'RECOVERY') && controlConsequenceId
          ? controlConsequenceId
          : undefined
      );
      setControlTitle('');
      setControlDesc('');
      await loadRisk();
      onUpdated();
    } catch (err: any) {
      showError(err, 'Failed to Add Safety Control');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateControl = async (controlId: string, status: string, score: number) => {
    if (!risk) return;
    try {
      setActionLoading(true);
      await riskService.updateControlStatus(risk.id, controlId, status, score);
      await loadRisk();
      onUpdated();
    } catch (err: any) {
      showError(err, 'Failed to Update Safety Control');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemoveControl = async (controlId: string) => {
    if (!risk || !confirm('Remove this safety control barrier?')) return;
    try {
      setActionLoading(true);
      await riskService.removeControl(risk.id, controlId);
      await loadRisk();
      onUpdated();
    } catch (err: any) {
      showError(err, 'Failed to Remove Safety Control');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddAssessment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!risk || !assessJustification.trim()) {
      showWarning('Assessment justification is required.', 'Justification Required');
      return;
    }

    try {
      setActionLoading(true);
      await riskService.addAssessment(
        risk.id,
        assessType,
        assessSev,
        assessLik,
        assessJustification.trim(),
        'USR-ASSESSOR',
        assessorName.trim()
      );
      setAssessJustification('');
      await loadRisk();
      onUpdated();
      showSuccess('Risk assessment recorded and residual scores updated!');
    } catch (err: any) {
      showError(err, 'Failed to Record Risk Assessment');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRecordReview = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!risk || !reviewNotes.trim()) {
      showWarning('Review notes / reasoning are required.', 'Review Notes Required');
      return;
    }

    try {
      setActionLoading(true);
      await riskService.recordReview(
        risk.id,
        'USR-REV-01',
        reviewerName.trim(),
        reviewType,
        reviewVerdict,
        reviewNotes.trim()
      );
      setReviewNotes('');
      await loadRisk();
      onUpdated();
      showSuccess('Management Review verdict successfully recorded.');
    } catch (err: any) {
      showError(err, 'Failed to Record Management Review');
    } finally {
      setActionLoading(false);
    }
  };

  const handleLinkHazard = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!risk || !hazardToLink) return;

    try {
      setActionLoading(true);
      await riskService.linkHazard(risk.id, hazardToLink, hazardLinkNote.trim() || undefined);
      setHazardToLink('');
      setHazardLinkNote('');
      await loadRisk();
      onUpdated();
    } catch (err: any) {
      showError(err, 'Failed to Link Hazard');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnlinkHazard = async (hazardId: string) => {
    if (!risk || !confirm('Unlink this hazard from risk dossier?')) return;
    try {
      setActionLoading(true);
      await riskService.unlinkHazard(risk.id, hazardId);
      await loadRisk();
      onUpdated();
    } catch (err: any) {
      showError(err, 'Failed to Unlink Hazard');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!risk || !newComment.trim()) return;

    try {
      setActionLoading(true);
      await riskService.addComment(risk.id, newComment.trim(), 'USR-SAFETY', 'Safety Coordinator');
      setNewComment('');
      await loadRisk();
    } catch (err: any) {
      showError(err, 'Failed to Add Comment');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading && !risk) {
    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(15, 23, 42, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}
      >
        <div style={{ background: '#ffffff', padding: '24px', borderRadius: '8px', color: '#64748b' }}>
          Loading Risk Dossier...
        </div>
      </div>
    );
  }

  if (!risk) return null;

  const getTolerabilityColor = (tol?: RiskTolerability) => {
    if (tol === 'INTOLERABLE') return { bg: '#fee2e2', text: '#b91c1c', border: '#fca5a5' };
    if (tol === 'TOLERABLE') return { bg: '#fef3c7', text: '#b45309', border: '#fde68a' };
    return { bg: '#dcfce7', text: '#15803d', border: '#86efac' };
  };

  const initTolStyle = getTolerabilityColor(risk.initialTolerability);
  const residTolStyle = getTolerabilityColor(risk.residualTolerability);

  return (
    <div className="modal-backdrop">
      <div
        className="modal-card max-w-5xl"
        style={{
          height: '92vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '20px 24px',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            background: '#f8fafc',
          }}
        >
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
              <span style={{ fontSize: '16px', fontWeight: 800, color: '#0284c7' }}>
                {risk.riskNumber}
              </span>
              <span
                style={{
                  background: '#f1f5f9',
                  color: '#334155',
                  padding: '2px 8px',
                  borderRadius: '4px',
                  fontSize: '11px',
                  fontWeight: 700,
                  border: '1px solid #cbd5e1',
                }}
              >
                {risk.status}
              </span>
            </div>
            <h2 style={{ fontSize: '18px', fontWeight: 700, margin: 0, color: '#0f172a' }}>
              {risk.title}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="modal-close-btn"
            title="Kapat"
            aria-label="Kapat"
          >
            <X size={20} />
          </button>
        </div>

        {/* Tab Navigation */}
        <div
          style={{
            display: 'flex',
            borderBottom: '1px solid #e2e8f0',
            background: '#ffffff',
            padding: '0 24px',
            gap: '8px',
            overflowX: 'auto',
          }}
        >
          {[
            { id: 'overview', label: 'Overview & Matrix' },
            { id: 'bowtie', label: '🎀 Bowtie Diagram' },
            { id: 'controls', label: `Controls & Barriers (${risk.controls?.length || 0})` },
            { id: 'reassessment', label: `Re-Assessment (${risk.assessments?.length || 0})` },
            { id: 'reviews', label: `Management Signoff (${risk.reviews?.length || 0})` },
            { id: 'hazards', label: `Linked Hazards (${risk.hazardLinks?.length || 0})` },
            { id: 'comments', label: `Notes (${risk.comments?.length || 0})` },
            { id: 'history', label: 'Audit Trail' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as TabType)}
              style={{
                padding: '12px 14px',
                border: 'none',
                background: 'transparent',
                fontSize: '13px',
                fontWeight: activeTab === tab.id ? 700 : 500,
                color: activeTab === tab.id ? '#0284c7' : '#64748b',
                borderBottom: activeTab === tab.id ? '2px solid #0284c7' : '2px solid transparent',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Modal Body / Tab Content */}
        <div style={{ overflowY: 'auto', padding: '24px', flex: 1 }}>
          {/* TAB 1: OVERVIEW */}
          {activeTab === 'overview' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {/* Risk Indices Comparison Card */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr auto 1fr',
                  alignItems: 'center',
                  gap: '16px',
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '10px',
                  padding: '20px',
                }}
              >
                {/* Initial Risk Card */}
                <div
                  style={{
                    background: initTolStyle.bg,
                    border: `1px solid ${initTolStyle.border}`,
                    borderRadius: '8px',
                    padding: '16px',
                    textAlign: 'center',
                  }}
                >
                  <div style={{ fontSize: '11px', fontWeight: 700, color: '#475569', textTransform: 'uppercase' }}>
                    Initial Risk Index
                  </div>
                  <div style={{ fontSize: '28px', fontWeight: 900, color: initTolStyle.text, margin: '6px 0' }}>
                    {risk.initialRiskIndex}
                  </div>
                  <div
                    style={{
                      display: 'inline-block',
                      fontSize: '11px',
                      fontWeight: 700,
                      color: initTolStyle.text,
                      padding: '2px 8px',
                      borderRadius: '4px',
                      background: 'rgba(255,255,255,0.7)',
                    }}
                  >
                    {risk.initialTolerability}
                  </div>
                  <div style={{ fontSize: '11px', color: '#64748b', marginTop: '6px' }}>
                    Sev: {risk.initialSeverity} / Lik: {risk.initialLikelihood}
                  </div>
                </div>

                <div style={{ fontSize: '24px', color: '#94a3b8', fontWeight: 'bold' }}>
                  ➔
                </div>

                {/* Residual Risk Card */}
                <div
                  style={{
                    background: risk.residualRiskIndex ? residTolStyle.bg : '#f1f5f9',
                    border: `1px solid ${risk.residualRiskIndex ? residTolStyle.border : '#cbd5e1'}`,
                    borderRadius: '8px',
                    padding: '16px',
                    textAlign: 'center',
                  }}
                >
                  <div style={{ fontSize: '11px', fontWeight: 700, color: '#475569', textTransform: 'uppercase' }}>
                    Residual Risk Index (ALARP)
                  </div>
                  {risk.residualRiskIndex ? (
                    <>
                      <div style={{ fontSize: '28px', fontWeight: 900, color: residTolStyle.text, margin: '6px 0' }}>
                        {risk.residualRiskIndex}
                      </div>
                      <div
                        style={{
                          display: 'inline-block',
                          fontSize: '11px',
                          fontWeight: 700,
                          color: residTolStyle.text,
                          padding: '2px 8px',
                          borderRadius: '4px',
                          background: 'rgba(255,255,255,0.7)',
                        }}
                      >
                        {risk.residualTolerability}
                      </div>
                      <div style={{ fontSize: '11px', color: '#64748b', marginTop: '6px' }}>
                        Sev: {risk.residualSeverity} / Lik: {risk.residualLikelihood}
                      </div>
                    </>
                  ) : (
                    <div style={{ padding: '16px 0', color: '#94a3b8', fontSize: '13px', fontStyle: 'italic' }}>
                      Mitigations Pending Assessment
                    </div>
                  )}
                </div>
              </div>

              {/* Status Transition Control */}
              <div
                style={{
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  padding: '16px',
                  background: '#ffffff',
                }}
              >
                <h4 style={{ fontSize: '14px', fontWeight: 600, color: '#0f172a', margin: '0 0 12px 0' }}>
                  Lifecycle State Transition
                </h4>
                <form onSubmit={handleTransitionStatus} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  <div style={{ minWidth: '220px' }}>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>
                      Target Status
                    </label>
                    <select
                      value={selectedToStatus}
                      onChange={(e) => setSelectedToStatus(e.target.value as RiskStatus)}
                      style={{
                        width: '100%',
                        padding: '8px 10px',
                        borderRadius: '6px',
                        border: '1px solid #cbd5e1',
                        fontSize: '13px',
                      }}
                    >
                      {ALL_STATUSES.map((st) => (
                        <option key={st} value={st}>
                          {st.replace(/_/g, ' ')}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div style={{ flex: 1, minWidth: '250px' }}>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>
                      Reason / Justification *
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="e.g., Mitigations verified effective on flight sim test"
                      value={transitionReason}
                      onChange={(e) => setTransitionReason(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 10px',
                        borderRadius: '6px',
                        border: '1px solid #cbd5e1',
                        fontSize: '13px',
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>

                  <div style={{ alignSelf: 'flex-end' }}>
                    <button
                      type="submit"
                      disabled={actionLoading || selectedToStatus === risk.status}
                      style={{
                        padding: '8px 16px',
                        background: '#0284c7',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: 600,
                        cursor: actionLoading || selectedToStatus === risk.status ? 'not-allowed' : 'pointer',
                        opacity: selectedToStatus === risk.status ? 0.5 : 1,
                      }}
                    >
                      Apply Status Change
                    </button>
                  </div>
                </form>
              </div>

              {/* General Metadata Attributes */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '14px' }}>
                <div style={{ background: '#f8fafc', padding: '12px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                  <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 600 }}>Department</div>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#0f172a', marginTop: '2px' }}>
                    {risk.department || 'Not Assigned'}
                  </div>
                </div>

                <div style={{ background: '#f8fafc', padding: '12px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                  <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 600 }}>Risk Owner</div>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#0f172a', marginTop: '2px' }}>
                    {risk.ownerName || 'Unassigned'}
                  </div>
                </div>

                <div style={{ background: '#f8fafc', padding: '12px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                  <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 600 }}>Next Review Date</div>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#0f172a', marginTop: '2px' }}>
                    {risk.nextReviewDate ? new Date(risk.nextReviewDate).toLocaleDateString() : 'None Scheduled'}
                  </div>
                </div>
              </div>

              {/* Description */}
              {risk.description && (
                <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', padding: '16px' }}>
                  <h4 style={{ fontSize: '13px', fontWeight: 600, color: '#334155', margin: '0 0 8px 0' }}>
                    Operational Context & Analysis
                  </h4>
                  <div style={{ fontSize: '13px', color: '#475569', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                    {risk.description}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* TAB: BOWTIE DIAGRAM */}
          {activeTab === 'bowtie' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <BowtieTab
                risks={[risk]}
                initialRiskId={risk.id}
                onDiagramUpdated={() => {
                  loadRisk();
                  onUpdated();
                }}
              />
            </div>
          )}

          {/* TAB 2: CONTROLS & BARRIERS */}
          {activeTab === 'controls' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {/* Form to add barrier */}
              <div
                style={{
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  padding: '16px',
                  background: '#f8fafc',
                }}
              >
                <h4 style={{ fontSize: '14px', fontWeight: 600, color: '#0f172a', margin: '0 0 12px 0' }}>
                  + Add Safety Control Barrier (Preventive / Mitigative)
                </h4>
                <form onSubmit={handleAddControl} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '12px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>
                        Barrier Title *
                      </label>
                      <input
                        type="text"
                        required
                        placeholder="e.g., Mandatory Simulator Training for Crosswind Landing"
                        value={controlTitle}
                        onChange={(e) => setControlTitle(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '8px 10px',
                          borderRadius: '6px',
                          border: '1px solid #cbd5e1',
                          fontSize: '13px',
                          boxSizing: 'border-box',
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>
                        Control Type
                      </label>
                      <select
                        value={controlType}
                        onChange={(e) => setControlType(e.target.value as any)}
                        style={{
                          width: '100%',
                          padding: '8px 10px',
                          borderRadius: '6px',
                          border: '1px solid #cbd5e1',
                          fontSize: '13px',
                        }}
                      >
                        <option value="PREVENTIVE">PREVENTIVE (Reduces Probability)</option>
                        <option value="MITIGATIVE">MITIGATIVE (Reduces Severity)</option>
                        <option value="RECOVERY">RECOVERY (Emergency / Contingency)</option>
                      </select>
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>
                        Expected Effectiveness (%)
                      </label>
                      <input
                        type="number"
                        min={0}
                        max={100}
                        value={controlEffectiveness}
                        onChange={(e) => setControlEffectiveness(parseInt(e.target.value) || 0)}
                        style={{
                          width: '100%',
                          padding: '8px 10px',
                          borderRadius: '6px',
                          border: '1px solid #cbd5e1',
                          fontSize: '13px',
                          boxSizing: 'border-box',
                        }}
                      />
                    </div>
                  </div>

                  {controlType === 'PREVENTIVE' ? (
                    <div>
                      <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>
                        Associated Threat / Causal Factor (Left Wing)
                      </label>
                      <select
                        value={controlThreatId}
                        onChange={(e) => setControlThreatId(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '8px 10px',
                          borderRadius: '6px',
                          border: '1px solid #cbd5e1',
                          fontSize: '13px',
                          background: '#ffffff',
                        }}
                      >
                        {bowtieThreats.map((t) => (
                          <option key={t.id} value={t.id}>
                            🟧 {t.label}
                          </option>
                        ))}
                        <option value="">Direct to Top Event / Hazard (General Barrier)</option>
                      </select>
                    </div>
                  ) : (
                    <div>
                      <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>
                        Associated Consequence / Outcome (Right Wing)
                      </label>
                      <select
                        value={controlConsequenceId}
                        onChange={(e) => setControlConsequenceId(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '8px 10px',
                          borderRadius: '6px',
                          border: '1px solid #cbd5e1',
                          fontSize: '13px',
                          background: '#ffffff',
                        }}
                      >
                        {bowtieConsequences.map((c) => (
                          <option key={c.id} value={c.id}>
                            🔴 {c.label}
                          </option>
                        ))}
                        <option value="">Direct from Top Event (General Mitigation Barrier)</option>
                      </select>
                    </div>
                  )}

                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>
                      Detailed Specification & Verification Criteria
                    </label>
                    <textarea
                      rows={2}
                      placeholder="Specify implementation procedures, standard operating procedures (SOP), or engineering modifications..."
                      value={controlDesc}
                      onChange={(e) => setControlDesc(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 10px',
                        borderRadius: '6px',
                        border: '1px solid #cbd5e1',
                        fontSize: '13px',
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button
                      type="submit"
                      disabled={actionLoading}
                      style={{
                        padding: '8px 16px',
                        background: '#0284c7',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      Add Barrier
                    </button>
                  </div>
                </form>
              </div>

              {/* Controls List */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <h4 style={{ fontSize: '14px', fontWeight: 600, color: '#0f172a', margin: 0 }}>
                  Active Barriers & Controls ({risk.controls?.length || 0})
                </h4>

                {(!risk.controls || risk.controls.length === 0) ? (
                  <div style={{ padding: '24px', textAlign: 'center', color: '#94a3b8', border: '1px dashed #cbd5e1', borderRadius: '8px' }}>
                    No safety control barriers currently registered. Add preventive or mitigative controls above to reduce risk to ALARP levels.
                  </div>
                ) : (
                  risk.controls.map((c) => {
                    const linkedThreat = c.threatId
                      ? bowtieThreats.find((t) => t.id === c.threatId)
                      : undefined;
                    const linkedCons = c.consequenceId
                      ? bowtieConsequences.find((cn) => cn.id === c.consequenceId)
                      : undefined;
                    return (
                      <div
                        key={c.id}
                        style={{
                          border: '1px solid #e2e8f0',
                          borderRadius: '8px',
                          padding: '14px 16px',
                          background: '#ffffff',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '8px',
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            <span
                              style={{
                                background: c.controlType === 'PREVENTIVE' ? '#dbeafe' : c.controlType === 'MITIGATIVE' ? '#fef3c7' : '#f3e8ff',
                                color: c.controlType === 'PREVENTIVE' ? '#1d4ed8' : c.controlType === 'MITIGATIVE' ? '#b45309' : '#7e22ce',
                                fontSize: '11px',
                                fontWeight: 700,
                                padding: '2px 8px',
                                borderRadius: '4px',
                              }}
                            >
                              {c.controlType}
                            </span>
                            <span style={{ fontSize: '14px', fontWeight: 600, color: '#0f172a' }}>
                              {c.title}
                            </span>
                            {linkedThreat && (
                              <span
                                style={{
                                  fontSize: '11px',
                                  fontWeight: 600,
                                  padding: '2px 8px',
                                  borderRadius: '4px',
                                  background: '#fff7ed',
                                  color: '#c2410c',
                                  border: '1px solid #fed7aa',
                                }}
                              >
                                🟧 Threat: {linkedThreat.label}
                              </span>
                            )}
                            {linkedCons && (
                              <span
                                style={{
                                  fontSize: '11px',
                                  fontWeight: 600,
                                  padding: '2px 8px',
                                  borderRadius: '4px',
                                  background: '#fef2f2',
                                  color: '#b91c1c',
                                  border: '1px solid #fecaca',
                                }}
                              >
                                🔴 Consequence: {linkedCons.label}
                              </span>
                            )}
                          </div>

                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <span style={{ fontSize: '12px', fontWeight: 600, color: '#475569' }}>
                              Effectiveness: {c.effectivenessScore}%
                            </span>
                            <select
                              value={c.status}
                              onChange={(e) => handleUpdateControl(c.id, e.target.value, c.effectivenessScore)}
                              style={{
                                padding: '4px 8px',
                                borderRadius: '4px',
                                border: '1px solid #cbd5e1',
                                fontSize: '12px',
                                background: '#f8fafc',
                              }}
                            >
                              <option value="PLANNED">PLANNED</option>
                              <option value="IN_PROGRESS">IN PROGRESS</option>
                              <option value="IMPLEMENTED">IMPLEMENTED</option>
                              <option value="VERIFIED_EFFECTIVE">VERIFIED EFFECTIVE</option>
                              <option value="INEFFECTIVE">INEFFECTIVE</option>
                            </select>
                            <button
                              onClick={() => handleRemoveControl(c.id)}
                              style={{
                                background: 'transparent',
                                border: 'none',
                                color: '#ef4444',
                                cursor: 'pointer',
                                fontSize: '14px',
                              }}
                              title="Delete control barrier"
                            >
                              ✕
                            </button>
                          </div>
                        </div>

                        {c.description && (
                          <div style={{ fontSize: '13px', color: '#64748b' }}>
                            {c.description}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}

          {/* TAB 3: RE-ASSESSMENT & RESIDUAL CALCULATOR */}
          {activeTab === 'reassessment' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div
                style={{
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  padding: '16px',
                  background: '#f8fafc',
                }}
              >
                <h4 style={{ fontSize: '14px', fontWeight: 600, color: '#0f172a', margin: '0 0 12px 0' }}>
                  Conduct Re-Assessment (Calculate Residual Risk)
                </h4>

                <form onSubmit={handleAddAssessment} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>
                        Assessment Type
                      </label>
                      <select
                        value={assessType}
                        onChange={(e) => setAssessType(e.target.value as any)}
                        style={{
                          width: '100%',
                          padding: '8px 10px',
                          borderRadius: '6px',
                          border: '1px solid #cbd5e1',
                          fontSize: '13px',
                        }}
                      >
                        <option value="RESIDUAL">RESIDUAL (Post-Mitigation ALARP)</option>
                        <option value="PERIODIC_REVIEW">PERIODIC REVIEW (Routine Safety Audit)</option>
                        <option value="INITIAL">INITIAL (Baseline Re-baseline)</option>
                      </select>
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>
                        Severity (1-5)
                      </label>
                      <select
                        value={assessSev}
                        onChange={(e) => setAssessSev(parseInt(e.target.value))}
                        style={{
                          width: '100%',
                          padding: '8px 10px',
                          borderRadius: '6px',
                          border: '1px solid #cbd5e1',
                          fontSize: '13px',
                        }}
                      >
                        {SEVERITY_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>
                        Likelihood (A-E)
                      </label>
                      <select
                        value={assessLik}
                        onChange={(e) => setAssessLik(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '8px 10px',
                          borderRadius: '6px',
                          border: '1px solid #cbd5e1',
                          fontSize: '13px',
                        }}
                      >
                        {LIKELIHOOD_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>
                      Assessment Justification / Evidence *
                    </label>
                    <textarea
                      rows={2}
                      required
                      placeholder="Explain how implemented controls and barriers have demonstrably reduced severity or probability..."
                      value={assessJustification}
                      onChange={(e) => setAssessJustification(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 10px',
                        borderRadius: '6px',
                        border: '1px solid #cbd5e1',
                        fontSize: '13px',
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button
                      type="submit"
                      disabled={actionLoading}
                      style={{
                        padding: '8px 18px',
                        background: '#0284c7',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      Save Assessment & Recalculate
                    </button>
                  </div>
                </form>
              </div>

              {/* Historical Assessments */}
              <div>
                <h4 style={{ fontSize: '14px', fontWeight: 600, color: '#0f172a', margin: '0 0 10px 0' }}>
                  Assessment Records ({risk.assessments?.length || 0})
                </h4>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {risk.assessments?.map((a) => (
                    <div
                      key={a.id}
                      style={{
                        padding: '12px 16px',
                        border: '1px solid #e2e8f0',
                        borderRadius: '6px',
                        background: '#ffffff',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontWeight: 700, fontSize: '14px', color: '#0f172a' }}>
                            Index: {a.riskIndex}
                          </span>
                          <span
                            style={{
                              fontSize: '11px',
                              fontWeight: 700,
                              color: a.tolerability === 'INTOLERABLE' ? '#b91c1c' : a.tolerability === 'TOLERABLE' ? '#b45309' : '#15803d',
                              background: a.tolerability === 'INTOLERABLE' ? '#fee2e2' : a.tolerability === 'TOLERABLE' ? '#fef3c7' : '#dcfce7',
                              padding: '1px 6px',
                              borderRadius: '4px',
                            }}
                          >
                            {a.tolerability}
                          </span>
                          <span style={{ fontSize: '12px', color: '#64748b' }}>({a.assessmentType})</span>
                        </div>
                        <div style={{ fontSize: '12px', color: '#334155', marginTop: '4px' }}>
                          {a.justification}
                        </div>
                      </div>

                      <div style={{ textAlign: 'right', fontSize: '11px', color: '#64748b' }}>
                        <div>Assessor: {a.assessorName}</div>
                        <div>{new Date(a.assessedAt).toLocaleDateString()}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* TAB 4: MANAGEMENT REVIEWS & SIGNOFF */}
          {activeTab === 'reviews' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div
                style={{
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  padding: '16px',
                  background: '#f8fafc',
                }}
              >
                <h4 style={{ fontSize: '14px', fontWeight: 600, color: '#0f172a', margin: '0 0 12px 0' }}>
                  Execute Management Review & Formal Acceptance
                </h4>

                {risk.initialTolerability === 'INTOLERABLE' && (!risk.residualTolerability || risk.residualTolerability === 'INTOLERABLE') && (
                  <div
                    style={{
                      background: '#fee2e2',
                      border: '1px solid #fca5a5',
                      color: '#b91c1c',
                      borderRadius: '6px',
                      padding: '10px 12px',
                      fontSize: '12px',
                      marginBottom: '12px',
                      fontWeight: 500,
                    }}
                  >
                    🔒 <strong>Strict Governance Warning:</strong> Current risk tolerability is INTOLERABLE. Under ICAO Annex 19 & Doc 9859, only the <strong>Accountable Manager</strong> holds legal authority to accept operations under elevated risk conditions.
                  </div>
                )}

                <form onSubmit={handleRecordReview} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>
                        Reviewer Authority *
                      </label>
                      <input
                        type="text"
                        required
                        value={reviewerName}
                        onChange={(e) => setReviewerName(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '8px 10px',
                          borderRadius: '6px',
                          border: '1px solid #cbd5e1',
                          fontSize: '13px',
                          boxSizing: 'border-box',
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>
                        Review Forum / Body
                      </label>
                      <select
                        value={reviewType}
                        onChange={(e) => setReviewType(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '8px 10px',
                          borderRadius: '6px',
                          border: '1px solid #cbd5e1',
                          fontSize: '13px',
                        }}
                      >
                        <option value="Safety Committee">Safety Review Committee (SRC)</option>
                        <option value="Accountable Manager">Accountable Manager Direct Executive Signoff</option>
                        <option value="Safety Action Group">Safety Action Group (SAG)</option>
                        <option value="Quality Assurance">Quality Assurance Audit Team</option>
                      </select>
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>
                        Official Verdict *
                      </label>
                      <select
                        value={reviewVerdict}
                        onChange={(e) => setReviewVerdict(e.target.value as any)}
                        style={{
                          width: '100%',
                          padding: '8px 10px',
                          borderRadius: '6px',
                          border: '1px solid #cbd5e1',
                          fontSize: '13px',
                        }}
                      >
                        <option value="ACCEPTED">ACCEPTED (Residual Risk Tolerable / ALARP)</option>
                        <option value="REJECTED_FURTHER_MITIGATION_REQUIRED">REJECTED (Further Barriers Required)</option>
                        <option value="ESCALATED_TO_ACCOUNTABLE_MGR">ESCALATED TO ACCOUNTABLE MANAGER</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>
                      Review Notes & Formal Acceptance Statement *
                    </label>
                    <textarea
                      rows={2}
                      required
                      placeholder="Detail considerations regarding ALARP demonstration, operational benefit vs safety margin, and ongoing monitoring stipulations..."
                      value={reviewNotes}
                      onChange={(e) => setReviewNotes(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 10px',
                        borderRadius: '6px',
                        border: '1px solid #cbd5e1',
                        fontSize: '13px',
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button
                      type="submit"
                      disabled={actionLoading}
                      style={{
                        padding: '8px 18px',
                        background: '#0284c7',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      Record Review & Update Status
                    </button>
                  </div>
                </form>
              </div>

              {/* Review History */}
              <div>
                <h4 style={{ fontSize: '14px', fontWeight: 600, color: '#0f172a', margin: '0 0 10px 0' }}>
                  Review History ({risk.reviews?.length || 0})
                </h4>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {risk.reviews?.map((rev) => (
                    <div
                      key={rev.id}
                      style={{
                        padding: '12px 16px',
                        border: '1px solid #e2e8f0',
                        borderRadius: '6px',
                        background: '#ffffff',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span
                            style={{
                              fontSize: '11px',
                              fontWeight: 700,
                              color: rev.verdict === 'ACCEPTED' ? '#15803d' : '#b91c1c',
                              background: rev.verdict === 'ACCEPTED' ? '#dcfce7' : '#fee2e2',
                              padding: '2px 8px',
                              borderRadius: '4px',
                            }}
                          >
                            {rev.verdict}
                          </span>
                          <span style={{ fontSize: '13px', fontWeight: 600, color: '#0f172a' }}>
                            {rev.reviewerName} ({rev.reviewType})
                          </span>
                        </div>
                        <span style={{ fontSize: '11px', color: '#64748b' }}>
                          {new Date(rev.reviewedAt).toLocaleDateString()}
                        </span>
                      </div>
                      <div style={{ fontSize: '12px', color: '#475569' }}>
                        {rev.notes}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* TAB 5: LINKED HAZARDS */}
          {activeTab === 'hazards' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div
                style={{
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  padding: '16px',
                  background: '#f8fafc',
                }}
              >
                <h4 style={{ fontSize: '14px', fontWeight: 600, color: '#0f172a', margin: '0 0 12px 0' }}>
                  Link Safety Hazard
                </h4>
                <form onSubmit={handleLinkHazard} style={{ display: 'flex', gap: '12px', alignItems: 'flex-end' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>
                      Select Hazard
                    </label>
                    <select
                      value={hazardToLink}
                      onChange={(e) => setHazardToLink(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 10px',
                        borderRadius: '6px',
                        border: '1px solid #cbd5e1',
                        fontSize: '13px',
                      }}
                    >
                      <option value="">-- Choose Hazard --</option>
                      {allHazards.map((h) => (
                        <option key={h.id} value={h.id}>
                          {h.hazardNumber} - {h.title}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>
                      Relationship Note
                    </label>
                    <input
                      type="text"
                      placeholder="e.g., Primary source hazard for unstabilized approaches"
                      value={hazardLinkNote}
                      onChange={(e) => setHazardLinkNote(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 10px',
                        borderRadius: '6px',
                        border: '1px solid #cbd5e1',
                        fontSize: '13px',
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={!hazardToLink || actionLoading}
                    style={{
                      padding: '8px 16px',
                      background: '#0284c7',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '13px',
                      fontWeight: 600,
                      cursor: !hazardToLink ? 'not-allowed' : 'pointer',
                    }}
                  >
                    Link Hazard
                  </button>
                </form>
              </div>

              <div>
                <h4 style={{ fontSize: '14px', fontWeight: 600, color: '#0f172a', margin: '0 0 10px 0' }}>
                  Connected Hazards ({risk.hazardLinks?.length || 0})
                </h4>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {risk.hazardLinks?.map((hl) => (
                    <div
                      key={hl.id}
                      style={{
                        padding: '12px 16px',
                        border: '1px solid #e2e8f0',
                        borderRadius: '6px',
                        background: '#ffffff',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: 600, fontSize: '13px', color: '#0284c7' }}>
                          {hl.hazardNumber || hl.hazardId} {hl.hazardTitle ? ` - ${hl.hazardTitle}` : ''}
                        </div>
                        {hl.relationshipNote && (
                          <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                            {hl.relationshipNote}
                          </div>
                        )}
                      </div>
                      <button
                        onClick={() => handleUnlinkHazard(hl.hazardId)}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: '#ef4444',
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontWeight: 600,
                        }}
                      >
                        Unlink
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* TAB 6: COMMENTS */}
          {activeTab === 'comments' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <form onSubmit={handleAddComment} style={{ display: 'flex', gap: '10px' }}>
                <input
                  type="text"
                  placeholder="Add a technical comment or coordination note..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  style={{
                    flex: 1,
                    padding: '9px 12px',
                    borderRadius: '6px',
                    border: '1px solid #cbd5e1',
                    fontSize: '13px',
                  }}
                />
                <button
                  type="submit"
                  disabled={!newComment.trim() || actionLoading}
                  style={{
                    padding: '9px 18px',
                    background: '#0284c7',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontWeight: 600,
                    cursor: !newComment.trim() ? 'not-allowed' : 'pointer',
                  }}
                >
                  Post Note
                </button>
              </form>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {risk.comments?.map((cm) => (
                  <div
                    key={cm.id}
                    style={{
                      padding: '12px 14px',
                      background: '#f8fafc',
                      borderRadius: '6px',
                      border: '1px solid #e2e8f0',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                      <span style={{ fontSize: '12px', fontWeight: 600, color: '#0f172a' }}>
                        {cm.userName}
                      </span>
                      <span style={{ fontSize: '11px', color: '#94a3b8' }}>
                        {new Date(cm.createdAt).toLocaleString()}
                      </span>
                    </div>
                    <div style={{ fontSize: '13px', color: '#334155' }}>
                      {cm.commentText}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* TAB 7: AUDIT TRAIL / STATUS HISTORY */}
          {activeTab === 'history' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <h4 style={{ fontSize: '14px', fontWeight: 600, color: '#0f172a', margin: '0 0 10px 0' }}>
                Immutable Status History & Compliance Log
              </h4>

              <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '12px' }}>
                  <thead>
                    <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#64748b' }}>
                      <th style={{ padding: '10px 14px' }}>Timestamp</th>
                      <th style={{ padding: '10px 14px' }}>From Status</th>
                      <th style={{ padding: '10px 14px' }}>To Status</th>
                      <th style={{ padding: '10px 14px' }}>Reason / Justification</th>
                      <th style={{ padding: '10px 14px' }}>Changed By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {risk.statusHistory?.map((h) => (
                      <tr key={h.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '10px 14px', color: '#64748b' }}>
                          {new Date(h.changedAt).toLocaleString()}
                        </td>
                        <td style={{ padding: '10px 14px', fontWeight: 600, color: '#475569' }}>
                          {h.fromStatus}
                        </td>
                        <td style={{ padding: '10px 14px', fontWeight: 700, color: '#0284c7' }}>
                          {h.toStatus}
                        </td>
                        <td style={{ padding: '10px 14px', color: '#0f172a' }}>
                          {h.reason || '-'}
                        </td>
                        <td style={{ padding: '10px 14px', color: '#64748b' }}>
                          {h.changedBy}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
